/**
 * OpenInAppBanner - Shows "Open in App" prompt for mobile web users
 *
 * Only shown on mobile browsers (iOS Safari, Android Chrome), NOT on:
 * - Desktop browsers
 * - Native Capacitor app
 *
 * This helps mobile users open magic links in the native app for a better experience.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import './OpenInAppBanner.css';

interface OpenInAppBannerProps {
  /** Callback when user chooses to continue in browser */
  onContinueInBrowser?: () => void;
}

/**
 * Detect if running on mobile web (not native app, not desktop)
 */
function isMobileWeb(): boolean {
  // Not mobile web if running in native Capacitor app
  if (Capacitor.isNativePlatform()) {
    return false;
  }

  // Check user agent for mobile devices
  const userAgent = navigator.userAgent;
  const isMobileDevice = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);

  return isMobileDevice;
}

/**
 * Build the app URL scheme link
 */
function buildAppLink(path: string, search: string): string {
  // Use custom URL scheme to open native app
  return `xavior:/${path}${search}`;
}

const BANNER_DISMISSED_KEY = 'xavior_app_banner_dismissed';
const CONTINUE_IN_BROWSER_EVENT = 'xavior:continue-in-browser';

/**
 * Dispatch event when user chooses to continue in browser
 */
function dispatchContinueInBrowserEvent(): void {
  window.dispatchEvent(new CustomEvent(CONTINUE_IN_BROWSER_EVENT));
}

/**
 * Listen for the "continue in browser" event
 */
export function onContinueInBrowser(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(CONTINUE_IN_BROWSER_EVENT, handler);
  return () => window.removeEventListener(CONTINUE_IN_BROWSER_EVENT, handler);
}

export function OpenInAppBanner({ onContinueInBrowser }: OpenInAppBannerProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Determine if we should show the banner
  useEffect(() => {
    // Only show on mobile web
    if (!isMobileWeb()) {
      setShowBanner(false);
      return;
    }

    // Only show on magic link verify page
    if (!location.pathname.startsWith('/auth/verify')) {
      setShowBanner(false);
      return;
    }

    // Check if already dismissed this session
    const wasDismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY);
    if (wasDismissed) {
      setShowBanner(false);
      return;
    }

    // Show the banner
    setShowBanner(true);
  }, [location.pathname]);

  const handleOpenApp = useCallback(() => {
    const appLink = buildAppLink(location.pathname, location.search);
    console.log('[OpenInAppBanner] Opening app with URL:', appLink);

    // Try to open the app
    window.location.href = appLink;

    // After a short delay, if we're still here, the app isn't installed
    // The user can still use the "Continue in Browser" option
  }, [location.pathname, location.search]);

  const handleContinueInBrowser = useCallback(() => {
    console.log('[OpenInAppBanner] User chose to continue in browser');
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setDismissed(true);
    setShowBanner(false);
    // Dispatch event so MagicLinkVerify can start verification
    dispatchContinueInBrowserEvent();
    onContinueInBrowser?.();
  }, [onContinueInBrowser]);

  // Don't render if not showing
  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <div className="open-in-app-banner">
      <div className="open-in-app-banner-content">
        <div className="open-in-app-banner-icon">📱</div>
        <div className="open-in-app-banner-text">
          <span className="open-in-app-banner-title">Continue in the XAVIOR app?</span>
          <span className="open-in-app-banner-subtitle">Better experience with the native app</span>
        </div>
      </div>
      <div className="open-in-app-banner-actions">
        <button
          className="open-in-app-banner-btn open-in-app-banner-btn-primary"
          onClick={handleOpenApp}
        >
          Open App
        </button>
        <button
          className="open-in-app-banner-btn open-in-app-banner-btn-secondary"
          onClick={handleContinueInBrowser}
        >
          Continue in Browser
        </button>
      </div>
    </div>
  );
}

/**
 * Synchronously check if we should pause for the banner
 * This must be synchronous to prevent race conditions with verification
 */
function shouldPauseForBannerSync(pathname: string): boolean {
  // Only pause on mobile web
  if (!isMobileWeb()) {
    return false;
  }

  // Only pause on the verify page
  if (!pathname.startsWith('/auth/verify')) {
    return false;
  }

  // Don't pause if banner was already dismissed
  const wasDismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY);
  if (wasDismissed) {
    return false;
  }

  return true;
}

/**
 * Hook to check if the banner is being shown
 * Used by MagicLinkVerify to pause verification while banner is visible
 *
 * IMPORTANT: Uses synchronous initial state to prevent race condition
 * where verification starts before we detect mobile web
 */
export function useShouldPauseForBanner(): boolean {
  const location = useLocation();

  // Calculate initial value synchronously to prevent race condition
  const [shouldPause, setShouldPause] = useState(() =>
    shouldPauseForBannerSync(location.pathname)
  );

  useEffect(() => {
    setShouldPause(shouldPauseForBannerSync(location.pathname));
  }, [location.pathname]);

  return shouldPause;
}

/**
 * Mark the banner as dismissed (used when user chooses to continue in browser)
 */
export function dismissAppBanner(): void {
  sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
}
